import React, { useState, useEffect, useMemo } from 'react';
import AcessoList from '../components/AcessoList';
import AcessoForm from '../components/AcessoForm';
import ConsultaAcessoLiberado from '../components/ConsultaAcessoLiberado';
import api from '../services/api';
import {
  Container, Typography, Box, Button, IconButton, Collapse, TextField, Autocomplete,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LoginIcon from '@mui/icons-material/Login';
import { debounce } from 'lodash';
import { actionButtonSx, filterCardSx, pageTitleSx, modalSurfaceSx } from '../styles/uiTokens';

function AcessosPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editingAcesso, setEditingAcesso] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showConsulta, setShowConsulta] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openTipoDialog, setOpenTipoDialog] = useState(false);

  // Filter states
  const [entradaStart, setEntradaStart] = useState('');
  const [entradaEnd, setEntradaEnd] = useState('');
  const [selectedImovelId, setSelectedImovelId] = useState('');
  const [selectedPessoaId, setSelectedPessoaId] = useState('');

  // Autocomplete states for Imovel filter
  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelFilter, setSelectedImovelFilter] = useState(null);

  // Autocomplete states for Pessoa (Visitante) filter
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [selectedPessoaFilter, setSelectedPessoaFilter] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then(response => setImovelOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar imoveis para filtro:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  const debouncedFetchPessoas = useMemo(() => debounce((searchTerm) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then(response => setPessoaOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar pessoas para filtro:', error))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchImoveis.cancel();
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchImoveis, debouncedFetchPessoas]);

  useEffect(() => {
    setRefreshList(prev => !prev);
  }, [entradaStart, entradaEnd, selectedImovelId, selectedPessoaId]);

  const handleAcessoAdded = () => {
    setRefreshList(prev => !prev);
    setEditingAcesso(null);
    setShowForm(false);
  };

  const handleEditAcesso = (acesso) => {
    setEditingAcesso(acesso);
    setShowForm(true);
  };

  const handleCancelEditAcesso = () => {
    setEditingAcesso(null);
    setShowForm(false);
  };

  const handleAcessoDeleted = () => {
    setRefreshList(prev => !prev);
  };

  const handleOpenTipoDialog = () => {
    setEditingAcesso(null);
    setOpenTipoDialog(true);
  };

  const handleCloseTipoDialog = () => setOpenTipoDialog(false);

  const handleEscolherNormal = () => {
    setOpenTipoDialog(false);
    setShowConsulta(false);
    setShowForm(true);
  };

  const handleEscolherLiberado = () => {
    setOpenTipoDialog(false);
    setShowForm(false);
    setShowConsulta(true);
  };

  const mostrarLista = !showForm && !showConsulta;

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>
          Portaria - Controle de Acesso
        </Typography>
        <Box>
          {mostrarLista && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenTipoDialog}
                sx={{ ...actionButtonSx, mr: 1 }}
              >
                Adicionar Acesso
              </Button>
              <IconButton
                onClick={() => setShowFilters(prev => !prev)}
                aria-label="toggle filters"
                sx={{ color: 'primary.main' }}
              >
                <FilterListIcon />
              </IconButton>
            </>
          )}
          {showConsulta && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setShowConsulta(false)}
            >
              Voltar
            </Button>
          )}
        </Box>
      </Box>

      {mostrarLista && (
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Entrada (Inicio)"
              type="datetime-local"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={entradaStart}
              onChange={(e) => setEntradaStart(e.target.value)}
            />
            <TextField
              label="Entrada (Fim)"
              type="datetime-local"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={entradaEnd}
              onChange={(e) => setEntradaEnd(e.target.value)}
            />
            <Autocomplete
              id="imovel-filter-autocomplete"
              options={imovelOptions}
              getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedImovelFilter}
              onChange={(event, newValue) => {
                setSelectedImovelFilter(newValue);
                setSelectedImovelId(newValue ? newValue.id : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) {
                    debouncedFetchImoveis(newInputValue);
                  } else {
                    setImovelOptions([]);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Imovel"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              id="pessoa-filter-autocomplete"
              options={pessoaOptions}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedPessoaFilter}
              onChange={(event, newValue) => {
                setSelectedPessoaFilter(newValue);
                setSelectedPessoaId(newValue ? newValue.id : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) {
                    debouncedFetchPessoas(newInputValue);
                  } else {
                    setPessoaOptions([]);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visitante"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <AcessoForm
          onAcessoAdded={handleAcessoAdded}
          initialData={editingAcesso}
          onCancelEdit={handleCancelEditAcesso}
        />
      )}

      {showConsulta && <ConsultaAcessoLiberado />}

      {mostrarLista && (
        <AcessoList
          refresh={refreshList}
          onEdit={handleEditAcesso}
          onAcessoDeleted={handleAcessoDeleted}
          entradaStart={entradaStart}
          entradaEnd={entradaEnd}
          imovelId={selectedImovelId}
          pessoaId={selectedPessoaId}
        />
      )}

      <Dialog
        open={openTipoDialog}
        onClose={handleCloseTipoDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: modalSurfaceSx }}
      >
        <DialogTitle>Escolher tipo de acesso</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione como deseja registrar a entrada.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={handleEscolherNormal}
              sx={actionButtonSx}
            >
              Acesso Normal
            </Button>
            <Button
              variant="outlined"
              startIcon={<VerifiedUserIcon />}
              onClick={handleEscolherLiberado}
            >
              Acesso Liberado
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTipoDialog} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AcessosPage;
